import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AppRole } from "@/types/supabase";
import { toast } from "sonner";
import { useSessionManagement } from "@/hooks/useSessionManagement";
import { useMFAReminder } from "@/contexts/MFAReminderContext";
import { isLoginNotificationEnabled } from "@/utils/emailSettingsUtils";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  userRole: AppRole | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
  refreshUserRole: (userId?: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  userRole: null,
  isLoading: true,
  signOut: async () => {},
  refreshUserRole: async () => {},
});

export const useAuth = () => useContext(AuthContext);

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  const loginNoticeSentRef = useRef(false);
  const { setShowMFAReminder, shouldShowReminder } = useMFAReminder();

  // Initialize session management for authenticated users
  useSessionManagement(user);
  // Function to send login notice email with per-login-event deduplication
  const sendLoginNotice = useCallback(async (user: User) => {
    try {
      // Check if login notifications are enabled
      const notificationsEnabled = await isLoginNotificationEnabled();
      if (!notificationsEnabled) {
        console.log("Login notifications are disabled, skipping login notice");
        return;
      }

      const loginEventAt = user.last_sign_in_at;
      if (!loginEventAt) {
        console.log("No last_sign_in_at timestamp, skipping login notice");
        return;
      }

      const sessionKey = `ps_login_notice_session_${user.id}`;
      const loginEventKey = `ps_login_notice_event_${user.id}_${loginEventAt}`;

      // Per-session guard to prevent rapid duplicates within the same session
      if (sessionStorage.getItem(sessionKey) === "1") {
        console.log("Skipping login notice (already sent this session)");
        return;
      }

      // Per-login-event guard using last_sign_in_at timestamp
      if (localStorage.getItem(loginEventKey) === "1") {
        console.log(
          `Skipping login notice (already sent for login event ${loginEventAt})`,
        );
        return;
      }

      const loginTime = new Date().toLocaleString("en-NG", {
        dateStyle: "full",
        timeStyle: "medium",
      });

      // Get user display name from public profile
      const { data: profile } = await supabase
        .from("public_profiles")
        .select("display_name")
        .eq("id", user.id)
        .single();

      const userName =
        profile?.display_name || user.email?.split("@")[0] || "User";

      console.log(
        `Sending login notice for user: ${user.id}, login event: ${loginEventAt}`,
      );

      // Set session guard immediately to avoid rapid duplicates
      sessionStorage.setItem(sessionKey, "1");

      const { error } = await supabase.functions.invoke("send-login-notice", {
        body: {
          user_id: user.id,
          user_email: user.email,
          user_name: userName,
          login_time: loginTime,
          login_event_at: loginEventAt,
        },
      });

      if (error) {
        console.error("Edge function reported error:", error);
        // Clear session guard so a retry can happen later if needed
        sessionStorage.removeItem(sessionKey);
        return;
      }

      // Mark as sent for this login event
      localStorage.setItem(loginEventKey, "1");
      console.log("Login notice email sent successfully");
    } catch (error) {
      console.error("Failed to send login notice:", error);
      // Don't throw error to avoid blocking authentication flow
    }
  }, []);
  // Memoized role fetching function to prevent unnecessary recreations
  const fetchUserRole = useCallback(
    async (userId: string): Promise<AppRole | null> => {
      console.log(`fetchUserRole - Fetching role for user:`, userId);

      try {
        // First check public profiles (faster query, indexed)
        const { data: profileData, error: profileError } = await supabase
          .from("public_profiles")
          .select("role")
          .eq("id", userId)
          .single();

        if (profileData?.role) {
          console.log(
            "fetchUserRole - Role found in public_profiles:",
            profileData.role,
          );
          return profileData.role as AppRole;
        }

        // Fallback to user_roles if no public profile
        if (profileError?.code === "PGRST116") {
          console.log("fetchUserRole - Checking user_roles");
          const { data: userRoleData, error: userRoleError } = await supabase
            .from("user_roles")
            .select("role")
            .eq("user_id", userId)
            .single();

          if (userRoleData?.role) {
            console.log(
              "fetchUserRole - Role found in user_roles:",
              userRoleData.role,
            );
            return userRoleData.role as AppRole;
          }
        }

        // No role found anywhere — validate session to detect deleted accounts
        if (profileError && profileError.code !== "PGRST116") {
          console.warn(
            "fetchUserRole - Unexpected error, validating session:",
            profileError,
          );
          const { error: userError } = await supabase.auth.getUser();
          if (userError) {
            console.warn(
              "fetchUserRole - Session invalid (user likely deleted), signing out",
            );
            await supabase.auth.signOut();
            return null;
          }
        }

        return null;
      } catch (error) {
        console.error("fetchUserRole - Error:", error);
        return null;
      }
    },
    [],
  );

  const refreshUserRole = useCallback(
    async (userId?: string) => {
      const targetId = userId || user?.id;
      if (!targetId) {
        setUserRole(null);
        return;
      }

      const role = await fetchUserRole(targetId);
      setUserRole(role);
    },
    [user?.id, fetchUserRole],
  );

  // Initialize auth state with improved performance
  useEffect(() => {
    console.log("🔐 AuthProvider - Initializing");
    let mounted = true;

    const initAuth = async () => {
      if (!mounted) return;

      try {
        // Get session without waiting for role fetch
        const {
          data: { session: currentSession },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("🔐 AuthProvider - Error getting session:", error);
        }

        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setInitialized(true);

          if (currentSession?.user) {
            // Wait for role before marking as ready so UI doesn't flicker
            const role = await fetchUserRole(currentSession.user.id);
            if (mounted) {
              setUserRole(role);
              setIsLoading(false);
            }
          } else {
            setUserRole(null);
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error("🔐 AuthProvider - Error initializing auth:", error);
        if (mounted) {
          setSession(null);
          setUser(null);
          setUserRole(null);
          setInitialized(true);
          setIsLoading(false);
        }
      }
    };

    // Optimized auth state listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      console.log("🔐 AuthProvider - Auth state changed:", event);

      if (mounted && initialized) {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (event === "SIGNED_OUT") {
          setUserRole(null);
        } else if (event === "SIGNED_IN" && currentSession?.user) {
          // Send login notice email when user signs in (guarded)
          setTimeout(() => {
            if (mounted && !loginNoticeSentRef.current) {
              loginNoticeSentRef.current = true;
              sendLoginNotice(currentSession.user);

              fetchUserRole(currentSession.user.id).then(async (role) => {
                if (mounted) {
                  setUserRole(role);

                  // Check MFA status and show reminder if needed
                  try {
                    // Skip reminder for users who haven't accepted terms yet
                    // (the Terms modal must be handled first to avoid clashing)
                    const { data: termsRow } = await supabase
                      .from("terms_acceptance")
                      .select("user_id")
                      .eq("user_id", currentSession.user.id)
                      .maybeSingle();
                    if (!termsRow) return;

                    const { data: profile } = await supabase
                      .from("public_profiles")
                      .select("mfa_enabled")
                      .eq("id", currentSession.user.id)
                      .single();

                    const mfaEnabled = profile?.mfa_enabled || false;

                    // Show MFA reminder for users without MFA who should be reminded
                    if (!mfaEnabled && shouldShowReminder()) {
                      setTimeout(() => setShowMFAReminder(true), 5000); // Let user settle, give terms modal priority
                    }
                  } catch (error) {
                    console.error("Error checking MFA status:", error);
                  }
                }
              });
            }
          }, 0);
        } else if (currentSession?.user) {
          // Fetch role in background for better performance
          setTimeout(() => {
            if (mounted) {
              fetchUserRole(currentSession.user.id).then((role) => {
                if (mounted) setUserRole(role);
              });
            }
          }, 0);
        } else {
          setUserRole(null);
        }
      }
    });

    initAuth();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [initialized, fetchUserRole]);

  const signOut = useCallback(async () => {
    try {
      console.log("🔐 AuthProvider - Signing out user");

      // Clear login notice session guard to allow email on next login
      if (user) {
        sessionStorage.removeItem(`ps_login_notice_session_${user.id}`);
      }

      // Reset login notice sent ref
      loginNoticeSentRef.current = false;

      // Clear state first for immediate UI feedback
      setUser(null);
      setSession(null);
      setUserRole(null);

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("🔐 AuthProvider - Error signing out:", error);
        toast.error("Error signing out. Please try again.");
        return;
      }
    } catch (error) {
      console.error("🔐 AuthProvider - Exception during sign out:", error);
      toast.error("Error signing out. Please try again.");
    }
  }, [user]);

  // Memoize the context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      session,
      user,
      userRole,
      isLoading,
      signOut,
      refreshUserRole,
    }),
    [session, user, userRole, isLoading, signOut, refreshUserRole],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
